<template>
	<v-data-table-server
		v-if="!useOfflineMode"
		:data-test="`${dataTest}-datatable`"
		v-model="selected"
		:page="page"
		:items-per-page="itemsPerPage"
		:items-per-page-options="itemsPerPageOptions"
		:expanded="expandedItems"
		:single-expand="true"
		:expand-on-click="false"
		:items-length="itemCount"
		:headers="headers"
		:items="items"
		item-value="id"
		:loading="loading"
		:sort-by="sortBy"
		:show-select="useSelect"
		:show-expand="!!definition?.expand || !!definition?.multiple"
		must-sort
		:disable-sort="false"
		striped="odd"
		hover
		:return-object="returnObject"
		@update:options="handleUpdateOptions"
	>
		<template #item="{ item, columns, ...slotProps }">
			<slot
				name="row"
				v-bind="{
					item,
					columns,
					isSelected: isSelected(item),
					expanded: expandedItems,
					showExpand: !!definition?.expand || !!definition?.multiple,
					isExpanded: expandedItems.includes(item.id),
					shouldShowLink: shouldShowLink(item),
					definition,
					updateSelection: (selected) => updateSelection(selected, item),
				}"
			>
				<TableRow
					:item="item"
					:columns="columns"
					:is-selected="isSelected(item)"
					v-model:expanded="expandedItems"
					:use-select="useSelect"
					:is-expanded="expandedItems.includes(returnObject ? item : item.id)"
					:show-expand="!!definition?.expand || !!definition?.multiple"
					:should-show-link="shouldShowLink(item)"
					:definition="definition"
					:return-object="returnObject"
					@update:selected="updateSelection($event, item)"
					@navigate-to-child="$emit('navigateToChild', $event)"
				>
					<template
						v-if="$slots['item.data-table-expand']"
						#expand-control="slotProps"
					>
						<slot
							name="item.data-table-expand"
							:item="slotProps.item"
							:is-expanded="
								() =>
									expandedItems.includes(
										returnObject ? slotProps.item : slotProps.item.id
									)
							"
						></slot>
					</template>
				</TableRow>
			</slot>
		</template>

		<template #expanded-row="{ columns, item }">
			<tr class="v-data-table__tr--expanded">
				<td
					:colspan="
						columns.length +
						(useSelect ? 1 : 0) +
						(!!definition?.expand || !!definition?.multiple ? 1 : 0)
					"
				>
					<slot
						name="expanded-row"
						:columns="columns"
						:item="item"
						:definition="definition.multiple"
						:data="
							definition.multiple
								? item[definition.multiple.linkItem]
								: undefined
						"
					>
						<DataTable
							v-if="definition?.multiple"
							:definition="definition.multiple"
							:endpoint="
								typeof definition.multiple?.endpoint === 'undefined'
									? definition.multiple.linkItem
									: definition.multiple?.endpoint
							"
							:hide-columns="definition.multiple?.hideColumns"
							:prop-data="item[definition.multiple.linkItem]"
							hide-header
							outlined
						></DataTable>
					</slot>
				</td>
			</tr>
		</template>

		<template #loading>
			<v-skeleton-loader
				v-if="
					items.length === 0 ||
					(loading && !usePlaceHolderData) ||
					(items.length > 0 && !usePlaceHolderData)
				"
				:type="`table-row@${itemsPerPage}`"
				><NoData
					:name="`${name}--datatable`"
					:error="error"
					:loading="loading"
				></NoData
			></v-skeleton-loader>
		</template>

		<template #no-data>
			<NoData
				:name="`${name}--datatable`"
				:error="error"
				:loading="loading"
			></NoData>
		</template>
	</v-data-table-server>

	<!-- Offline mode table -->
	<template v-else>
		<v-data-table
			v-model="selected"
			:headers="headers"
			:items="cachedItems"
			:sort-by="virtualSortBy"
			:search="localSearch"
			:show-select="useSelect"
			:show-expand="!!definition?.expand || !!definition?.multiple"
			:return-object="returnObject"
			:items-length="cachedItems?.length"
			:page="offlinePage"
			:items-per-page="offlineItemsPerPage"
			:items-per-page-options="itemsPerPageOptions"
			striped="odd"
			hover
			@update:sort-by="handleSortChange"
			@update:options="handleOfflineOptions"
		>
			<template #item="{ item, columns, ...slotProps }">
				<slot
					name="row"
					v-bind="{
						item,
						columns,
						isSelected: isSelected(item),
						expanded: expandedItems,
						showExpand: !!definition?.expand || !!definition?.multiple,
						isExpanded: expandedItems.includes(item.id),
						shouldShowLink: shouldShowLink(item),
						definition,
						updateSelection: (selected) => updateSelection(selected, item),
					}"
				>
					<TableRow
						:item="item"
						:columns="columns"
						:is-selected="isSelected(item)"
						v-model:expanded="expandedItems"
						:use-select="useSelect"
						:is-expanded="expandedItems.includes(returnObject ? item : item.id)"
						:show-expand="!!definition?.expand || !!definition?.multiple"
						:should-show-link="shouldShowLink(item)"
						:definition="definition"
						:return-object="returnObject"
						@update:selected="updateSelection($event, item)"
						@navigate-to-child="$emit('navigateToChild', $event)"
					>
						<template
							v-if="$slots['item.data-table-expand']"
							#expand-control="slotProps"
						>
							<slot
								name="item.data-table-expand"
								:item="slotProps.item"
								:is-expanded="
									() =>
										expandedItems.includes(
											returnObject ? slotProps.item : slotProps.item.id
										)
								"
							></slot>
						</template>
					</TableRow>
				</slot>
			</template>

			<template #loading>
				<v-skeleton-loader
					v-if="
						cachedItems?.length === 0 ||
						(loading && !usePlaceHolderData) ||
						(cachedItems?.length > 0 && !usePlaceHolderData)
					"
					:type="`table-row@${itemsPerPage}`"
				></v-skeleton-loader>
			</template>

			<template #no-data>
				<NoData
					:error="error"
					:loading="loading"
					:name="`${name}--datatable`"
				></NoData>
			</template>
		</v-data-table>
	</template>

	<!-- No data state when no cached items available -->
	<!--	<template v-else>
		<div class="text-center pa-8">
			<v-icon size="64" color="grey-lighten-1" class="mb-4">
				mdi-dastabase-off
			</v-icon>
			<h3 class="text-h6 mb-2">No Cached Data Available</h3>
			<p class="text-body-2 text-medium-emphasis">
				No data is available offline. Please connect to the internet to load
				data.
			</p>
		</div>
	</template>-->
</template>

<script lang="ts" setup>
import type {
	DataTableDefinition,
	DataTableHeader,
	DataTableHeaderProps,
	SortItem,
} from '@/types/table.ts'
import { computed, ref } from 'vue'
import { storeToRefs } from 'pinia'
import { isEqual } from 'lodash'
import { useUserStore } from '@/stores/system/user'
import DataTable from '@/components/templates/Datatable/DataTable.vue'
import TableRow from './TableRow.vue'
import NoData from './NoData.vue'

interface TableItem {
	id: string | number
	[key: string]: any
}

interface TableProps {
	modelValue?: any[]
	items: any[]
	headers: DataTableHeader[]
	loading?: boolean
	itemCount?: number
	page?: number
	itemsPerPage?: number
	itemsPerPageOptions?: number[]
	sortBy?: SortItem[]
	definition: DataTableDefinition
	expanded: any[]
	returnObject?: boolean
	useSelect?: boolean
	error?: any
	usePlaceHolderData?: boolean
	enableOfflineMode?: boolean
	cachedItems?: any[] // All items from TanStack cache (across all pages)
	localSearch?: string
	name: string
	dataTest: string
}

const props = withDefaults(defineProps<TableProps & DataTableHeaderProps>(), {
	modelValue: () => [],
	loading: false,
	itemCount: undefined,
	page: 1,
	itemsPerPage: 25,
	itemsPerPageOptions: () => [25, 50, 100],
	sortBy: () => [],
	expanded: () => [],
	returnObject: true,
	useSelect: false,
	error: null,
	usePlaceHolderData: false,
	enableOfflineMode: true,
	cachedItems: undefined,
	localSearch: '',
})

const emit = defineEmits<{
	'update:modelValue': [value: any[]]
	'update:page': [page: number]
	'update:itemsPerPage': [itemsPerPage: number]
	'update:sortBy': [sortBy: SortItem[]]
	'update:expanded': [expanded: any[]]
	'update:selected': any[]
	navigateToChild: [item: TableItem]
}>()

const { isOnline } = storeToRefs(useUserStore())
const virtualSortBy = ref<SortItem[]>(props.sortBy || [])

// Offline pagination state
const offlinePage = ref(1)
const offlineItemsPerPage = ref(props.itemsPerPage || 25)

// Check if we should use offline mode
const useOfflineMode = computed(() => {
	return (
		props.enableOfflineMode &&
		!isOnline.value /*&& props.cachedItems?.length > 0*/
	)
})
const selected = computed({
	get: () => props.modelValue,
	set: (value) => emit('update:modelValue', value),
})

const expandedItems = computed({
	get: () => {
		if (props.returnObject) {
			return props.expanded
		}
		return props.expanded.map((item) => item.id)
	},
	set: (value) => emit('update:expanded', value),
})

function handleUpdateOptions(options: any): void {
	if (useOfflineMode.value) return // Don't emit server updates in offline mode

	if (options.page !== props.page) {
		emit('update:page', options.page)
	}
	if (options.itemsPerPage !== props.itemsPerPage) {
		emit('update:itemsPerPage', options.itemsPerPage)
	}
	if (!isEqual(options.sortBy, props.sortBy)) {
		emit('update:sortBy', options.sortBy)
	}
}

function handleSortChange(sortBy: SortItem[]): void {
	virtualSortBy.value = sortBy
}

function handleOfflineOptions(options: any): void {
	if (options.page !== offlinePage.value) {
		offlinePage.value = options.page
	}
	if (options.itemsPerPage !== offlineItemsPerPage.value) {
		offlineItemsPerPage.value = options.itemsPerPage
		offlinePage.value = 1 // Reset to first page when changing items per page
	}
}

function updateSelection(selected: boolean, item: any): void {
	const newSelected = [...(props.modelValue || [])]
	const index = newSelected.findIndex((i) =>
		props.returnObject ? i.id === item.id : i === item.id
	)

	if (selected && index === -1) {
		newSelected.push(props.returnObject ? item : item.id)
	} else if (!selected && index !== -1) {
		newSelected.splice(index, 1)
	}

	emit('update:modelValue', newSelected)
}

function shouldShowLink(item: TableItem): boolean {
	if (!props.definition.child) return false

	const { multiple } = props.definition
	if (multiple && multiple.linkFirstItem !== true) {
		if (multiple.linkItem && item[multiple.linkItem]?.length !== 1) {
			return false
		}
	}
	return true
}

function isSelected(item: TableItem): boolean {
	return (
		selected.value &&
		selected.value.find((i) =>
			props.returnObject ? i.id === item.id : i === item.id
		) !== undefined
	)
}
</script>

<style scoped>
.offline-table-container {
	position: relative;
}

.v-table.v-table--hover
	> .v-table__wrapper
	> table
	> tbody
	> tr:hover
	> td::after {
	display: none !important;
}

.v-data-table-header__sort-icon {
	--v-icon-size-multiplier: 0.7;
}

/* AAR-962: kompaktere Auswahl-Spalte auf Mobile (xs) — Header-Padding der
   Select-Spalte trimmen, damit die Checkbox-Spalte nicht unnötig breit wird.
   Tap-Fläche der Checkbox bleibt unverändert. */
@media (max-width: 599.98px) {
	:deep(.v-data-table-column--no-padding) {
		width: 36px !important;
		min-width: 36px !important;
		max-width: 36px !important;
		padding-inline: 0 !important;
	}
	/* Auswahl-Checkboxen (Header-„alle"-Box + Zeilen) auf Mobile kompakter
	   fassen: das Icon bleibt 24px, nur die Hüll-/Tap-Fläche schrumpft auf
	   32px (immer noch größer als die bisherigen 24px der Zeilen-Checkbox). */
	:deep(.v-data-table-column--no-padding) .v-selection-control,
	:deep(td.select-cell) .v-selection-control {
		--v-selection-control-size: 32px;
	}
}

/* Custom row background classes */
.v-data-table {
	tbody {
		tr:hover:not(.v-data-table__tr--expanded) {
			background-color: rgb(var(--v-theme-primary)) !important;
			color: rgb(var(--v-theme-on-primary)) !important;
		}
	}
}
</style>
